import { c as _c } from "react/compiler-runtime";
import { memo, useCallback, useMemo, useRef } from 'react';
import { useKeyboard } from '@/hooks/keyboard/use-keyboard';
import { useMeasureOptions } from '@/hooks/store/use-measure-options-store';
import { useStickyHeight } from '@/hooks/use-sticky-height';
import { useTranslation } from '@/hooks/use-translation';
import { KeyColor } from '@/lib/piano/keyboard';
import { cn } from '@/lib/utils';
import { KeyMeasurement } from './KeyMeasurement';
import { useMeasurePasses, type MeasurePassId } from './hooks/use-measure-passes';
import { nextMeasureField } from './measure-tab-order';
export interface KeyMeasureListProps {
  currentKeyNumber: number | null;
  currentPassId: MeasurePassId;
  onFocusKey: (keyNumber: number | null) => void;
}
const keyClassName = 'inline-flex h-5 items-center rounded-[3px] border border-ptl-rule-strong px-1.5 font-mono text-[11px] text-foreground';

/** The field an event comes from: which key, which quantity. */
const fieldOf = (target: EventTarget) => {
  if (!(target instanceof HTMLInputElement) || !target.name) {
    return null;
  }
  const keyNumber = Number(target.closest<HTMLElement>('[data-key-number]')?.dataset.keyNumber);
  return Number.isInteger(keyNumber) ? {
    keyNumber,
    property: target.name
  } : null;
};

/*
 * The head's rules are drawn inside its cells: a table's collapsed borders stay
 * where the table lays them, and would let the rows show through a line once
 * the head sticks.
 */
const groupClassName = 'h-[30px] bg-card px-3 shadow-[inset_0_-1px_0_var(--border)] text-left align-middle font-mono text-[10.5px] font-normal tracking-[0.08em] text-ptl-ink-3 uppercase';
const headerClassName = 'h-12 bg-card px-3 text-left align-middle text-[13px] font-semibold shadow-[inset_0_-1px_0_var(--ptl-rule-strong)]';

/*
 * From 1280px the table fits its page without scrolling sideways, so its frame
 * need not be a scroll box, and the head can stick under the panel: a filled
 * field shows no abbreviation, and without it the columns go unnamed as soon
 * as the page has scrolled. Narrower, the frame scrolls sideways, and a sticky
 * head could only stick to the frame.
 */
const headClassName = 'xl:sticky xl:top-[var(--ptl-sticky-top,0px)] xl:z-[1]';

/**
 * The measure table: the readings, and nothing worked out from them.
 *
 * The balance weight and the friction are read in Analyze, where the springs
 * are counted in or not, and named so. Here they were a second figure under
 * the same name, beside the readings it came from.
 */
export const KeyMeasureList = memo(function KeyMeasureList(t0) {
  const $ = _c(93);
  const {
    currentKeyNumber,
    currentPassId,
    onFocusKey
  } = t0;
  const {
    t
  } = useTranslation();
  const {
    keyboard
  } = useKeyboard();
  let t1;
  if ($[0] !== keyboard) {
    t1 = keyboard.getKeys();
    $[0] = keyboard;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const keys = t1;
  const {
    useManualSWRMeasurements,
    useSupportSpringMeasurements
  } = useMeasureOptions();
  const passes = useMeasurePasses();
  const header = useRef(null);
  useStickyHeight(header, "--ptl-sticky-header");
  let t2;
  if ($[2] !== currentPassId || $[3] !== passes) {
    t2 = passes.find(pass => pass.id === currentPassId)?.fields ?? [];
    $[2] = currentPassId;
    $[3] = passes;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const activeFields = t2;
  let t3;
  if ($[5] !== passes) {
    t3 = passes.map(_temp);
    $[5] = passes;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const passFields = t3;
  let t4;
  if ($[7] !== keys.length || $[8] !== passFields) {
    t4 = event => {
      if (event.key !== "Tab" || event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      const field = fieldOf(event.target);
      if (!field) {
        return;
      }
      const next = nextMeasureField(passFields, keys.length, field, event.shiftKey ? -1 : 1);
      const input = next && document.querySelector(`[data-key-number="${next.keyNumber}"] input[name="${next.property}"]`);
      if (input) {
        event.preventDefault();
        input.focus();
      }
    };
    $[7] = keys.length;
    $[8] = passFields;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const onKeyDown = t4;
  let t5;
  if ($[10] !== onFocusKey) {
    t5 = event_0 => {
      const field_0 = fieldOf(event_0.target);
      if (field_0) {
        onFocusKey(field_0.keyNumber);
      }
    };
    $[10] = onFocusKey;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const onFocus = t5;
  let t10;
  let t11;
  let t12;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[12] !== activeFields || $[13] !== currentKeyNumber || $[14] !== currentPassId || $[15] !== keys || $[16] !== onFocus || $[17] !== onKeyDown || $[18] !== passes || $[19] !== t || $[20] !== useManualSWRMeasurements || $[21] !== useSupportSpringMeasurements) {
    const measuredColumns = [...(useSupportSpringMeasurements ? [{
      abbreviation: "DS",
      name: t("D with spring"),
      property: "downWeightWithSpringSupport" as const
    }] : []), {
      abbreviation: "D",
      name: t("Down weight"),
      property: "downWeightWithoutSpringSupport" as const
    }, {
      abbreviation: "U",
      name: t("Up weight"),
      property: "upWeight" as const
    }, {
      abbreviation: "FW",
      name: t("Front weight"),
      property: "frontWeight" as const
    }, {
      abbreviation: "SW",
      name: t("Strike weight"),
      property: "strikeWeight" as const
    }, ...(useManualSWRMeasurements ? [{
      abbreviation: "SWR",
      name: t("Ratio"),
      property: "measuredStrikeWeightRatio" as const
    }] : [])];
    if ($[29] !== t) {
      t10 = t("For each note");
      $[29] = t;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    t11 = "overflow-hidden rounded-md border border-ptl-rule-strong bg-card xl:overflow-visible print:overflow-visible print:rounded-none print:border-0";
    let t13;
    if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <th colSpan={2} className={cn(groupClassName, "max-xl:sticky max-xl:left-0 max-xl:z-[2]")} />;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== currentPassId || $[33] !== passes || $[34] !== t) {
      let t15;
      if ($[36] !== currentPassId || $[37] !== t) {
        t15 = pass_1 => {
          const isCurrent = pass_1.id === currentPassId;
          return <th key={pass_1.id} colSpan={pass_1.fields.length} scope="colgroup" className={cn(groupClassName, isCurrent && "bg-[linear-gradient(var(--accent),var(--accent))] text-foreground print:bg-none print:text-ptl-ink-3")}>{t("Pass {{number}}", {
              number: pass_1.number
            })}{isCurrent ? <span className="print:hidden">{` · ${t("Current")}`}</span> : null}</th>;
        };
        $[36] = currentPassId;
        $[37] = t;
        $[38] = t15;
      } else {
        t15 = $[38];
      }
      t14 = passes.map(t15);
      $[32] = currentPassId;
      $[33] = passes;
      $[34] = t;
      $[35] = t14;
    } else {
      t14 = $[35];
    }
    let t15;
    if ($[39] !== t14) {
      t15 = <tr>{t13}{t14}</tr>;
      $[39] = t14;
      $[40] = t15;
    } else {
      t15 = $[40];
    }
    let t16;
    if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = cn(headerClassName, "w-[52px] text-ptl-ink-3 max-xl:sticky max-xl:left-0 max-xl:z-[2]");
      $[41] = t16;
    } else {
      t16 = $[41];
    }
    let t17;
    if ($[42] !== t) {
      t17 = t("No.");
      $[42] = t;
      $[43] = t17;
    } else {
      t17 = $[43];
    }
    let t18;
    if ($[44] !== t17) {
      t18 = <th className={t16}>{t17}</th>;
      $[44] = t17;
      $[45] = t18;
    } else {
      t18 = $[45];
    }
    let t19;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t19 = cn(headerClassName, "w-[72px] text-ptl-ink-3 max-xl:sticky max-xl:left-[52px] max-xl:z-[2]");
      $[46] = t19;
    } else {
      t19 = $[46];
    }
    let t20;
    if ($[47] !== t) {
      t20 = t("Note");
      $[47] = t;
      $[48] = t20;
    } else {
      t20 = $[48];
    }
    let t21;
    if ($[49] !== t20) {
      t21 = <th className={t19}>{t20}</th>;
      $[49] = t20;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    const t22 = <tr>{t18}{t21}{measuredColumns.map(column => <th key={column.property} className={cn(headerClassName, !activeFields.includes(column.property) && "text-ptl-ink-3")}><span className="mr-2 font-mono text-[11.5px] font-medium text-ptl-brass">{column.abbreviation}</span>{column.name}</th>)}</tr>;
    let t23;
    if ($[51] !== t15 || $[52] !== t22) {
      t23 = <thead ref={header} className={headClassName}>{t15}{t22}</thead>;
      $[51] = t15;
      $[52] = t22;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    let t24;
    if ($[54] !== currentKeyNumber || $[55] !== currentPassId || $[56] !== keys) {
      let t25;
      if ($[58] !== currentKeyNumber || $[59] !== currentPassId) {
        t25 = key => <KeyMeasurement key={key.number} currentPassId={currentPassId} isBlackKey={key.color === KeyColor.Black} isCurrent={key.number === currentKeyNumber} keyName={key.name} keyNumber={key.number} />;
        $[58] = currentKeyNumber;
        $[59] = currentPassId;
        $[60] = t25;
      } else {
        t25 = $[60];
      }
      t24 = keys.map(t25);
      $[54] = currentKeyNumber;
      $[55] = currentPassId;
      $[56] = keys;
      $[57] = t24;
    } else {
      t24 = $[57];
    }
    let t25;
    if ($[61] !== onFocus || $[62] !== onKeyDown || $[63] !== t24) {
      t25 = <tbody onFocus={onFocus} onKeyDown={onKeyDown}>{t24}</tbody>;
      $[61] = onFocus;
      $[62] = onKeyDown;
      $[63] = t24;
      $[64] = t25;
    } else {
      t25 = $[64];
    }
    if ($[65] !== t23 || $[66] !== t25) {
      t12 = <div className="overflow-x-auto xl:overflow-visible print:overflow-visible"><table className="w-full min-w-[720px] border-collapse print:min-w-0">{t23}{t25}</table></div>;
      $[65] = t23;
      $[66] = t25;
      $[67] = t12;
    } else {
      t12 = $[67];
    }
    t9 = "flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-border px-4 py-3 text-[12.5px] text-muted-foreground print:hidden";
    t7 = "inline-flex flex-wrap items-center gap-1.5";
    if ($[68] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <kbd className={keyClassName}>Tab</kbd>;
      $[68] = t8;
    } else {
      t8 = $[68];
    }
    const t26 = activeFields.map(field_1 => measuredColumns.find(column_0 => column_0.property === field_1)?.abbreviation);
    let t27;
    if ($[69] !== t) {
      t27 = t("next key");
      $[69] = t;
      $[70] = t27;
    } else {
      t27 = $[70];
    }
    t6 = [...t26, t27];
    $[12] = activeFields;
    $[13] = currentKeyNumber;
    $[14] = currentPassId;
    $[15] = keys;
    $[16] = onFocus;
    $[17] = onKeyDown;
    $[18] = passes;
    $[19] = t;
    $[20] = useManualSWRMeasurements;
    $[21] = useSupportSpringMeasurements;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
    $[25] = t6;
    $[26] = t7;
    $[27] = t8;
    $[28] = t9;
  } else {
    t10 = $[22];
    t11 = $[23];
    t12 = $[24];
    t6 = $[25];
    t7 = $[26];
    t8 = $[27];
    t9 = $[28];
  }
  const t13 = t6.join(" \u2192 ");
  let t14;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span aria-hidden="true">·</span>;
    $[71] = t14;
  } else {
    t14 = $[71];
  }
  let t15;
  if ($[72] !== t) {
    t15 = t("Shift");
    $[72] = t;
    $[73] = t15;
  } else {
    t15 = $[73];
  }
  let t16;
  if ($[74] !== t15) {
    t16 = <kbd className={keyClassName}>{t15}</kbd>;
    $[74] = t15;
    $[75] = t16;
  } else {
    t16 = $[75];
  }
  let t17;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <kbd className={keyClassName}>Tab</kbd>;
    $[76] = t17;
  } else {
    t17 = $[76];
  }
  let t18;
  if ($[77] !== t) {
    t18 = t("back");
    $[77] = t;
    $[78] = t18;
  } else {
    t18 = $[78];
  }
  let t19;
  if ($[79] !== t13 || $[80] !== t16 || $[81] !== t18 || $[82] !== t7 || $[83] !== t8) {
    t19 = <span className={t7}>{t8}{t13}{t14}{t16}{t17}{t18}</span>;
    $[79] = t13;
    $[80] = t16;
    $[81] = t18;
    $[82] = t7;
    $[83] = t8;
    $[84] = t19;
  } else {
    t19 = $[84];
  }
  let t20;
  if ($[85] !== t19 || $[86] !== t9) {
    t20 = <div className={t9}>{t19}</div>;
    $[85] = t19;
    $[86] = t9;
    $[87] = t20;
  } else {
    t20 = $[87];
  }
  let t21;
  if ($[88] !== t10 || $[89] !== t11 || $[90] !== t12 || $[91] !== t20) {
    t21 = <section aria-label={t10} className={t11}>{t12}{t20}</section>;
    $[88] = t10;
    $[89] = t11;
    $[90] = t12;
    $[91] = t20;
    $[92] = t21;
  } else {
    t21 = $[92];
  }
  return t21;
});
function _temp(pass_0) {
  return pass_0.fields;
}